package net.sergeych.bintools

import kotlinx.serialization.KSerializer
import kotlinx.serialization.serializer
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.BipackEncoder
import kotlin.reflect.KProperty


/**
 * Generic storage of binary content. PArsec uses boss encoding to store everything in it
 * in a convenient way. See [KVStorage.stored], [KVStorage.invoke] and
 * [KVStorage.optStored] delegates. The [MemoryKVStorage] is an implementation that stores
 * values in memory, allowing to connect some other (e.g. persistent storage) later in a
 * completely transparent way. It can also be used to cache values on the fly.
 *
 * Also, it is possible to use [read] and [write] where delegated properties
 * do not fit well.
 */
@Suppress("unused")
interface KVStorage {
    operator fun get(key: String): ByteArray?
    operator fun set(key: String, value: ByteArray?)

    /**
     * Check whether key is in storage.
     * Default implementation uses [keys]. You may override it for performance
     */
    operator fun contains(key: String): Boolean = key in keys

    val keys: Set<String>


    /**
     * Get number of object in the storage
     * Default implementation uses [keys]. You may override it for performance
     */
    val size: Int get() = keys.size

    /**
     * Clears all objects in the storage
     * Default implementation uses [keys]. You may override it for performance
     */
    fun clear() {
        for (k in keys.toList()) this[k] = null
    }

    /**
     * Default implementation uses [keys]. You may override it for performance
     */
    fun isEmpty() = size == 0

    /**
     * Default implementation uses [keys]. You may override it for performance
     */
    fun isNotEmpty() = size != 0

    /**
     * Add all elements from another storage, overwriting any existing
     * keys.
     */
    fun addAll(other: KVStorage) {
        for (k in other.keys) {
            this[k] = other[k]
        }
    }

    /**
     * Delete element by key
     */
    fun delete(key: String) {
        set(key, null)
    }
}

/**
 * Write
 */
inline fun <reified T>KVStorage.write(key: String,value: T) {
    this[key] = BipackEncoder.encode<T>(value)
}

@Suppress("unused")
inline fun <reified T>KVStorage.save(key: String,value: T?) {
    if( value != null ) write(key, value)
    else delete(key)
}

inline fun <reified T:Any>KVStorage.read(key: String): T? =
    this[key]?.let { BipackDecoder.decode<T>(it) }

@Suppress("unused")
inline fun <reified T:Any>KVStorage.load(key: String): T? = read(key)

inline operator fun <reified T: Any> KVStorage.invoke(defaultValue: T,overrideName: String? = null) =
    KVStorageDelegate(this, serializer<T>(), defaultValue, overrideName)

inline fun <reified T: Any> KVStorage.stored(defaultValue: T, overrideName: String? = null) =
    KVStorageDelegate(this, serializer<T>(), defaultValue, overrideName)

inline fun <reified T: Any> KVStorage.optStored(overrideName: String? = null) =
    KVStorageOptDelegate<T>(this, serializer<T>(),overrideName)

class KVStorageDelegate<T: Any>(
    private val storage: KVStorage,
    private val serializer: KSerializer<T>,
    private val defaultValue: T,
    private val overrideName: String? = null,
) {
    private fun name(property: KProperty<*>): String = overrideName ?: property.name

    operator fun getValue(thisRef: Any?, property: KProperty<*>): T =
        storage.get(name(property))?.let { BipackDecoder.decode(serializer, it) }
            ?: defaultValue


    operator fun setValue(thisRef: Any?, property: KProperty<*>, value: T) {
        storage[name(property)] = BipackEncoder.encode(serializer, value)
    }
}

class KVStorageOptDelegate<T: Any>(
    private val storage: KVStorage,
    private val serializer: KSerializer<T>,
    private val overrideName: String? = null,
) {
    private fun name(property: KProperty<*>): String = overrideName ?: property.name

    operator fun getValue(thisRef: Any?, property: KProperty<*>): T? =
        storage.get(name(property))?.let{
            BipackDecoder.decode(serializer, it)
        }

    operator fun setValue(thisRef: Any?, property: KProperty<*>, value: T?) {
        if (value == null)
            storage.delete(name(property))
        else
            storage[name(property)] = BipackEncoder.encode(serializer, value)
    }
}

/**
 * Memory storage. Allows connecting to another storage (e.g. persistent one) at come
 * point later in a transparent way.
 */
class MemoryKVStorage(copyFrom: KVStorage? = null) : KVStorage {

    // is used when connected:
    private var underlying: KVStorage? = null

    // is used while underlying is null:
    private val data = mutableMapOf<String, ByteArray>()

    /**
     * Connect some other storage. All existing data will be copied to the [other]
     * storage. After this call all data access will be routed to [other] storage.
     */
    @Suppress("unused")
    fun connectToStorage(other: KVStorage) {
        other.addAll(this)
        underlying = other
        data.clear()
    }


    /**
     * Get  data from either memory or a connected storage, see [connectToStorage]
     */
    override fun get(key: String): ByteArray? {
        underlying?.let {
            return it[key]
        }
        return data[key]
    }

    /**
     * Put data to memory storage or connected storage if [connectToStorage] was called
     */
    override fun set(key: String, value: ByteArray?) {
        underlying?.let { it[key] = value } ?: run {
            if (value != null) data[key] = value
            else data.remove(key)
        }
    }

    /**
     * Checks the item exists in the memory storage or connected one, see[connectToStorage]
     */
    override fun contains(key: String): Boolean {
        underlying?.let { return key in it }
        return key in data
    }

    override val keys: Set<String>
        get() = underlying?.keys ?: data.keys

    override fun clear() {
        underlying?.clear() ?: data.clear()
    }

    init {
        copyFrom?.let { addAll(it) }
    }
}

/**
 * Create per-platform default named storage.
 *
 * - In the browser, it uses the `Window.localStorage` prefixing items
 *   by a string containing the [name]
 *
 * - In the JVM environment it uses folder-based storage on the file system. The name
 *   is considered to be a folder name (the whole path which will be automatically created)
 *   using the following rules:
 *    - when the name starts with slash (`/`) it is treated as an absolute path to a folder
 *    - when the name contains slash, it is considered to be a relative folder to the
 *      `User.home` directory, like "`~/`" on unix systems.
 *    - otherwise, the folder will be created in "`~/.local_storage`" parent directory
 *      (which also will be created if needed).
 *
 *  - In Android (new!) it is implemented using preferences, but initializing is needed in
 *      main activity `onCreate`, somewhat like:
 *  ```kotlin
 *       override fun onCreate(savedInstanceState: Bundle?) {
 *          super.onCreate(savedInstanceState)
 *          // set the Context which Preferences will be used for
 *          // defaultNamedStorage
 *          net.sergeych.bintools.AndroidKV.init(this)
 *     }
 *  ```
 *  see also `AndroidKV` and `SharedPreferencesKVStorage` for Android
 *
 *  - For the native platforms it is not yet implemented (but will be soon).
 *
 *  See [DataKVStorage] and [DataProvider] to implement a KVStorage on filesystems and like,
 *  and `FileDataProvider` class on JVM target.
 */
expect fun defaultNamedStorage(name: String): KVStorage